/**
 * GSORG API client — aligned with backend at chatbox.tanstudio.me
 * Base: window.__API_BASE__ || VITE_API_BASE || https://chatbox.tanstudio.me
 */

const DEFAULT_API = 'https://chatbox.tanstudio.me';
const TOKEN_KEY = 'gsorg_token';
const USER_KEY = 'gsorg_user';

export function getApiBase() {
  const fromWindow = typeof window !== 'undefined' && window.__API_BASE__;
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE;
  return String(fromWindow || fromEnv || DEFAULT_API).replace(/\/$/, '');
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setCachedUser(user) {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

export function clearSession() {
  setToken('');
  setCachedUser(null);
}

/** Strip leading zeros from numeric-looking GIDs (e.g. 00101 → 101) */
export function normalizeGid(gid) {
  if (gid == null) return '';
  const s = String(gid).trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) {
    const stripped = s.replace(/^0+/, '');
    return stripped === '' ? '0' : stripped;
  }
  return s;
}

/** Resolve relative media URLs against API base */
export function mediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:') || path.startsWith('data:')) return path;
  return `${getApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
}

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request(path, options = {}) {
  const {
    method = 'GET',
    body,
    auth = true,
    headers: extraHeaders = {},
    formData = false,
  } = options;

  const headers = { ...extraHeaders };
  if (!formData && body != null && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const url = `${getApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body:
        body == null
          ? undefined
          : formData || body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(err.message || '网络错误', 0, null);
  }

  const ct = res.headers.get('content-type') || '';
  let data = null;
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    data = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.message || data.msg)) || `请求失败 (${res.status})`;
    throw new ApiError(msg, res.status, data);
  }

  return data;
}

/* ---------- Auth ---------- */
export const auth = {
  register({ nickname, password, email, invite_code, turnstileToken }) {
    return request('/api/auth/register', {
      method: 'POST',
      auth: false,
      body: {
        nickname,
        password,
        email: email || undefined,
        invite_code,
        turnstileToken,
        'cf-turnstile-response': turnstileToken,
      },
    });
  },
  login({ gid, password, turnstileToken }) {
    return request('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: {
        gid: normalizeGid(gid),
        password,
        turnstileToken,
        'cf-turnstile-response': turnstileToken,
      },
    });
  },
  me() {
    return request('/api/auth/me');
  },
  updateMe(payload) {
    // backend: nickname, bio, email, avatar_url, cover_url, password
    return request('/api/auth/me', { method: 'PATCH', body: payload });
  },
};

/* ---------- Config ---------- */
export function getConfig() {
  return request('/api/config', { auth: false });
}

/* ---------- Feed & Posts ---------- */
export const posts = {
  feed(params = {}) {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request(`/api/feed${qs ? `?${qs}` : ''}`);
  },
  get(id) {
    return request(`/api/posts/${encodeURIComponent(id)}`);
  },
  create({ content, image_url }) {
    return request('/api/posts', {
      method: 'POST',
      body: { content, image_url: image_url || null },
    });
  },
  update(id, payload) {
    return request(`/api/posts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload,
    });
  },
  remove(id) {
    return request(`/api/posts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  like(id) {
    return request(`/api/posts/${encodeURIComponent(id)}/like`, { method: 'POST' });
  },
  unlike(id) {
    return request(`/api/posts/${encodeURIComponent(id)}/like`, { method: 'DELETE' });
  },
  comments(id) {
    return request(`/api/posts/${encodeURIComponent(id)}/comments`);
  },
  addComment(id, content) {
    return request(`/api/posts/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: { content },
    });
  },
};

/* ---------- Users ---------- */
export const users = {
  get(gid) {
    return request(`/api/users/${encodeURIComponent(gid)}`);
  },
  posts(gid, params = {}) {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit || 20));
    const qs = q.toString();
    return request(`/api/users/${encodeURIComponent(gid)}/posts${qs ? `?${qs}` : ''}`);
  },
  follow(gid) {
    return request(`/api/users/${encodeURIComponent(gid)}/follow`, { method: 'POST' });
  },
  unfollow(gid) {
    return request(`/api/users/${encodeURIComponent(gid)}/follow`, { method: 'DELETE' });
  },
  followers(gid) {
    return request(`/api/users/${encodeURIComponent(gid)}/followers`);
  },
  following(gid) {
    return request(`/api/users/${encodeURIComponent(gid)}/following`);
  },
};

/* ---------- Search ---------- */
export function search(q) {
  const params = new URLSearchParams({ q });
  return request(`/api/search?${params}`);
}

/* ---------- Notifications ---------- */
export const notifications = {
  list(params = {}) {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request(`/api/notifications${qs ? `?${qs}` : ''}`);
  },
  markRead(ids) {
    return request('/api/notifications/read', {
      method: 'POST',
      body: ids && ids.length ? { ids } : {},
    });
  },
};

/* ---------- Messages ---------- */
export const messages = {
  conversations() {
    return request('/api/messages/conversations');
  },
  thread(gid, params = {}) {
    const q = new URLSearchParams();
    if (params.before) q.set('before', params.before);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request(`/api/messages/${encodeURIComponent(gid)}${qs ? `?${qs}` : ''}`);
  },
  send({ to_gid, content, file_url, file_name }) {
    return request('/api/messages', {
      method: 'POST',
      body: { to_gid, content, file_url, file_name },
    });
  },
};

/* ---------- Upload ---------- */
export async function uploadFile(file, { type = 'image' } = {}) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('type', type);
  return request('/api/upload', { method: 'POST', body: fd, formData: true });
}

/* ---------- Admin ---------- */
export const admin = {
  listUsers(params = {}) {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request(`/api/admin/users${qs ? `?${qs}` : ''}`);
  },
  createUser(payload) {
    return request('/api/admin/users', { method: 'POST', body: payload });
  },
  updateUser(id, payload) {
    return request(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload,
    });
  },
  getSettings() {
    return request('/api/admin/settings');
  },
  updateSettings(payload) {
    return request('/api/admin/settings', { method: 'PATCH', body: payload });
  },
  broadcast({ message, to_gid, to_user_id, to_user_ids, to_all }) {
    return request('/api/admin/broadcast', {
      method: 'POST',
      body: { message, to_gid, to_user_id, to_user_ids, to_all },
    });
  },
  listInviteCodes() {
    return request('/api/admin/invite-codes');
  },
  createInviteCode(payload) {
    return request('/api/admin/invite-codes', { method: 'POST', body: payload });
  },
  removeInviteCode(id) {
    return request(`/api/admin/invite-codes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  listSensitiveWords() {
    return request('/api/admin/sensitive-words');
  },
  addSensitiveWord(word) {
    return request('/api/admin/sensitive-words', {
      method: 'POST',
      body: { word },
    });
  },
  removeSensitiveWord(id) {
    return request(`/api/admin/sensitive-words/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

export default {
  getApiBase,
  getToken,
  setToken,
  getCachedUser,
  setCachedUser,
  clearSession,
  normalizeGid,
  mediaUrl,
  request,
  auth,
  getConfig,
  posts,
  users,
  search,
  notifications,
  messages,
  uploadFile,
  admin,
  ApiError,
};
